import { type StockRow, WAREHOUSE_KINDS } from '@epc/contracts';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { tenge } from '../../lib/format';
import { ScrollX } from '../../ui/scroll';
import { qty } from './queries';

/**
 * Остатки: на телефоне — карточки, где остаток, резерв и «доступно» видны без прокрутки вбок,
 * на ПК — таблица. Столбец склада или позиции можно спрятать, когда страница про один из них.
 */
export function StockTable({ rows, hide }: { rows: StockRow[]; hide?: 'warehouse' | 'item' }) {
  const hasPrice = rows.some((r) => r.avgPrice !== null);
  return (
    <>
      <ul className="divide-y divide-line md:hidden">
        {rows.map((r) => (
          <li key={`${r.warehouseId}:${r.itemId}`} className="flex flex-col gap-1 px-4 py-3 text-[13px]">
            {hide !== 'item' ? (
              <Link
                to={`/warehouse/items/${r.itemId}`}
                className="text-[14px] font-medium text-ink hover:underline"
              >
                {r.itemName}
              </Link>
            ) : null}
            {hide !== 'warehouse' ? (
              <Link
                to={`/warehouse/warehouses/${r.warehouseId}`}
                className={clsx(
                  'hover:underline',
                  hide === 'item' ? 'text-[14px] font-medium text-ink' : 'text-ink-2',
                )}
              >
                {r.warehouseName}
                {r.projectCode ? ` · ${r.projectCode}` : ''}
              </Link>
            ) : null}
            <span className="num flex flex-wrap justify-between gap-x-3 text-ink-2">
              <span>
                остаток {qty(r.onHand)} {r.unit}
                {r.inQc ? ` · контроль ${qty(r.inQc)}` : ''}
                {r.reserved ? ` · резерв ${qty(r.reserved)}` : ''}
              </span>
              <span className={clsx('font-semibold', r.available > 0 ? 'text-ink' : 'text-danger')}>
                доступно {qty(r.available)}
              </span>
            </span>
            {r.avgPrice !== null ? (
              <span className="num text-[12px] text-ink-2">средняя цена {tenge(r.avgPrice)}</span>
            ) : null}
          </li>
        ))}
      </ul>
      <ScrollX className="hidden md:block">
        <table className="w-full min-w-[640px] text-[14px]">
          <thead>
            <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
              {hide !== 'item' ? <th className="py-2.5 pr-3 pl-5 font-medium">Позиция</th> : null}
              {hide !== 'warehouse' ? (
                <th className={clsx('py-2.5 pr-3 font-medium', hide === 'item' && 'pl-5')}>Склад</th>
              ) : null}
              <th className="py-2.5 pr-3 text-right font-medium">Остаток</th>
              <th className="py-2.5 pr-3 text-right font-medium">На контроле</th>
              <th className="py-2.5 pr-3 text-right font-medium">Резерв</th>
              <th className={clsx('py-2.5 text-right font-medium', hasPrice ? 'pr-3' : 'pr-5')}>Доступно</th>
              {hasPrice ? <th className="py-2.5 pr-5 text-right font-medium">Ср. цена</th> : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.warehouseId}:${r.itemId}`} className="border-t border-line align-top">
                {hide !== 'item' ? (
                  <td className="py-2.5 pr-3 pl-5">
                    <Link
                      to={`/warehouse/items/${r.itemId}`}
                      className="font-medium text-ink hover:underline"
                    >
                      {r.itemName}
                    </Link>
                    <div className="num text-[12px] text-ink-2">
                      {r.itemCode} · {r.unit}
                    </div>
                  </td>
                ) : null}
                {hide !== 'warehouse' ? (
                  <td className={clsx('py-2.5 pr-3', hide === 'item' && 'pl-5')}>
                    <Link to={`/warehouse/warehouses/${r.warehouseId}`} className="hover:underline">
                      {r.warehouseName}
                    </Link>
                    <div className="text-[12px] text-ink-2">
                      {WAREHOUSE_KINDS[r.warehouseKind]}
                      {r.projectCode ? ` · ${r.projectCode}` : ''}
                    </div>
                  </td>
                ) : null}
                <td className="num py-2.5 pr-3 text-right">{qty(r.onHand)}</td>
                <td className="num py-2.5 pr-3 text-right text-ink-2">{r.inQc ? qty(r.inQc) : '—'}</td>
                <td className="num py-2.5 pr-3 text-right text-ink-2">
                  {r.reserved ? qty(r.reserved) : '—'}
                </td>
                <td
                  className={clsx(
                    'num py-2.5 text-right font-semibold',
                    hasPrice ? 'pr-3' : 'pr-5',
                    r.available <= 0 && 'text-danger',
                  )}
                >
                  {qty(r.available)}
                </td>
                {hasPrice ? (
                  <td className="num py-2.5 pr-5 text-right">
                    {r.avgPrice === null ? '—' : tenge(r.avgPrice)}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollX>
    </>
  );
}
